import { ChevronDown, Search } from 'lucide-react';
import {
	Select as AriaSelect,
	Autocomplete,
	Button,
	Input,
	ListBox,
	ListBoxItem,
	Popover,
	SearchField,
	SelectValue,
	useFilter,
} from 'react-aria-components';
import { cn } from '../utils';

/**
 * A single-choice select styled to the DS `Input` tokens. Built on
 * react-aria-components so the dropdown is fully styled (no native <select>):
 * the trigger mirrors the Input look (grey `bg-zinc-50`, `border-none`,
 * `rounded-2xl`, `py-3.5 px-4`, `text-sm`, `focus:ring-2 ring-primary/50`) and
 * the popup mirrors the datetime `popupClass` surface, with day-cell-style
 * option highlighting (`hover:bg-zinc-100`, selected `bg-primary text-zinc-900`,
 * `rounded-xl`).
 *
 * `searchable` keeps the exact same `Select` trigger (click to open; the trigger
 * shows the selected option's label + chevron). The difference lives entirely
 * inside the open popover: a small search field is rendered above the options
 * list and filters the list as you type (the Radix/shadcn "select with
 * in-dropdown search" pattern). It is implemented with RAC's `Autocomplete`
 * primitive wrapping a `SearchField` + `ListBox` — `Autocomplete` owns the
 * focus management (the search input autofocuses on open), virtual focus / arrow
 * keys on the list, and the case-insensitive "contains" filter (via `useFilter`)
 * — while the typeahead that would otherwise hijack typing on the listbox is
 * suppressed because keystrokes go to the search input, not the collection.
 *
 * Both variants share the same trigger shell, chevron, popover surface and
 * option styling — the class strings below are factored out so the two paths
 * stay visually identical.
 */

export interface SelectOption {
	value: string;
	label: string;
}

export interface SelectProps {
	label?: string;
	required?: boolean;
	options: SelectOption[];
	value?: string | null;
	onChange?: (value: string) => void;
	placeholder?: string;
	error?: string;
	disabled?: boolean;
	/** When true (default), renders an in-dropdown search field above the options list. */
	searchable?: boolean;
}

// --- Shared class strings (keep the plain + searchable paths in sync) --------

const labelClass = 'mb-2 block text-sm font-medium text-zinc-700';

/** The Input-styled trigger shell. `error` toggles the invalid ring. */
const triggerShell = (error?: string) =>
	cn(
		'w-full flex items-center justify-between gap-2 bg-zinc-50 border-none rounded-2xl text-sm text-zinc-900 transition-all py-3.5 px-4 text-left',
		'outline-none focus:ring-2 focus-within:ring-2',
		error ? 'focus:ring-red-500/50 focus-within:ring-red-500/50 ring-2 ring-red-200' : 'focus:ring-primary/50 focus-within:ring-primary/50',
		'data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed',
	);

const chevronClass = 'shrink-0 text-zinc-400';
const errorTextClass = 'mt-1 text-xs text-red-500';

const popoverClass = cn(
	'bg-white rounded-2xl shadow-soft border border-zinc-200 p-1.5 z-[200]',
	'origin-top-left data-[entering]:opacity-0 data-[entering]:scale-95 transition-all duration-150',
	'w-[var(--trigger-width)] max-h-72 overflow-y-auto',
);

const listBoxClass = 'outline-none flex flex-col gap-0.5';

const listBoxItemClass = cn(
	'px-3 py-2 rounded-xl text-sm font-medium text-zinc-700 cursor-default outline-none transition-colors',
	'data-[hovered]:bg-zinc-100 data-[focused]:bg-zinc-100',
	'data-[selected]:bg-primary data-[selected]:text-zinc-900 data-[selected]:data-[hovered]:bg-primary',
);

function Label({ label, required }: { label?: string; required?: boolean }) {
	if (!label) return null;
	return (
		<label className={labelClass}>
			{label}
			{required && <span className="ml-0.5 text-red-500">*</span>}
		</label>
	);
}

function Options({ options }: { options: SelectOption[] }) {
	return (
		<>
			{options.map((opt) => (
				<ListBoxItem key={opt.value} id={opt.value} textValue={opt.label} className={listBoxItemClass}>
					{opt.label}
				</ListBoxItem>
			))}
		</>
	);
}

// --- In-dropdown search: the body rendered inside the Select's Popover --------

/** The subtle, sticky search field shown at the top of the searchable popover. */
const searchWrapClass = cn(
	'sticky top-0 z-10 mb-1.5 -mx-0.5 px-0.5 pb-1.5',
	'bg-white border-b border-zinc-100',
);

const searchFieldClass = cn(
	'flex items-center gap-2 rounded-xl bg-zinc-50 px-3 py-2',
	'focus-within:ring-2 focus-within:ring-primary/50',
);

const searchInputClass = 'flex-1 min-w-0 bg-transparent text-sm text-zinc-900 outline-none placeholder:text-zinc-400';

/**
 * The searchable popover body: a `SearchField` + `ListBox` wrapped in
 * `Autocomplete`. `Autocomplete` autofocuses the search input on mount (i.e. when
 * the popover opens — RAC remounts popover children each open), drives the
 * case-insensitive "contains" filter, and routes arrow/typeahead to the input so
 * the listbox's own typeahead can't hijack typing. The input value resets on
 * close because the popover (and this subtree) unmounts.
 */
function SearchableBody({ options }: { options: SelectOption[] }) {
	const { contains } = useFilter({ sensitivity: 'base' });
	return (
		<Autocomplete filter={contains}>
			<div className={searchWrapClass}>
				<SearchField aria-label="Search options" autoFocus className={searchFieldClass}>
					<Search size={16} className="shrink-0 text-zinc-400" aria-hidden />
					<Input
						placeholder="Search…"
						className={searchInputClass}
					/>
				</SearchField>
			</div>
			<ListBox
				items={options}
				className={listBoxClass}
				renderEmptyState={() => <p className="px-3 py-6 text-center text-sm text-zinc-400">No matches</p>}
			>
				{(opt: SelectOption) => (
					<ListBoxItem id={opt.value} textValue={opt.label} className={listBoxItemClass}>
						{opt.label}
					</ListBoxItem>
				)}
			</ListBox>
		</Autocomplete>
	);
}

export function Select(props: SelectProps) {
	const {
		label,
		required,
		options,
		value,
		onChange,
		placeholder = 'Select…',
		error,
		disabled,
		searchable = true,
	} = props;

	return (
		<AriaSelect
			selectedKey={value ?? null}
			onSelectionChange={(key) => onChange?.(String(key))}
			isDisabled={disabled}
			isInvalid={!!error}
			className="w-full"
		>
			<Label label={label} required={required} />
			<Button className={triggerShell(error)}>
				<SelectValue className="truncate data-[placeholder]:text-zinc-400" />
				<ChevronDown size={18} className={chevronClass} aria-hidden />
			</Button>
			{error && <p className={errorTextClass}>{error}</p>}
			<Popover className={popoverClass} maxHeight={288}>
				{searchable ? (
					<SearchableBody options={options} />
				) : (
					<ListBox className={listBoxClass}>
						<Options options={options} />
					</ListBox>
				)}
			</Popover>
		</AriaSelect>
	);
}
